import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useHistoryStore } from '@/lib/store/history-store';
import { useDeepSearchListener } from '@/lib/hooks/useDeepSearchListener';
import { SocialLinks } from '@/components/search/social-links';
import { Badge } from '@/components/ui/badge';
import type { DeepSearchData } from '@/lib/types';
import { ExternalLink, Image as ImageIcon, Video, Newspaper } from 'lucide-react';
import { MoreInfoSheet } from '@/components/search/more-info-sheet';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { logAnalyticsEvent } from '@/lib/firebase/analytics';

export default function SearchAnythingResultPage() {
  const { getSearch, userId } = useHistoryStore();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const hasLoggedComplete = useRef(false);

  // Listen for real-time updates from Firestore
  useDeepSearchListener(userId, id || '');

  useEffect(() => {
    document.title = 'Search Results - Deepcheck';
  }, []);

  const search = id ? getSearch(id) : null;
  const result = search?.data as DeepSearchData | undefined;

  // Log search_complete when results are available
  useEffect(() => {
    if (result && search?.status === 'completed' && !hasLoggedComplete.current) {
      logAnalyticsEvent('search_complete', { type: 'anything' });
      hasLoggedComplete.current = true;
    }
  }, [result, search?.status]);

  // Show loading while waiting for data
  if (!search || search.status === 'analyzing' || search.status === 'queued') {
    return (
      <main className="px-4 py-6 flex flex-col items-center justify-center min-h-[60vh]">
        <LoadingSpinner />
        <p className="mt-4 text-gray-600">Researching...</p>
        {search && <p className="text-sm text-gray-400 mt-2">Progress: {search.progress}%</p>}
      </main>
    );
  }

  // Show error state
  if (search.status === 'failed') {
    return (
      <main className="px-4 py-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h2 className="text-red-800 font-semibold">Search Failed</h2>
          <p className="text-red-600 text-sm mt-1">{search.error?.message || 'An error occurred'}</p>
        </div>
      </main>
    );
  }

  if (!result) {
    return (
      <main className="px-4 py-6">
        <LoadingSpinner />
      </main>
    );
  }

  return (
    <main className="px-4 py-6 space-y-6">
      {/* Header */}
      <h1 className="text-2xl font-bold text-gray-900">{result.title}</h1>

      {/* Social Profiles */}
      {result.socialProfiles && result.socialProfiles.length > 0 && (
        <SocialLinks profiles={result.socialProfiles} />
      )}

      {/* Findings Summary */}
      <div className="bg-gray-50 rounded-lg p-4 space-y-3">
        <h2 className="font-semibold text-gray-900">Findings</h2>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="bg-white p-3 rounded flex items-center gap-3">
            <Newspaper className="w-5 h-5 text-blue-500" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{result.newsArticles?.length || 0}</div>
              <div className="text-gray-600">News Articles</div>
            </div>
          </div>
          <div className="bg-white p-3 rounded flex items-center gap-3">
            <ImageIcon className="w-5 h-5 text-green-500" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{result.images?.length || 0}</div>
              <div className="text-gray-600">Images</div>
            </div>
          </div>
          <div className="bg-white p-3 rounded flex items-center gap-3">
            <Video className="w-5 h-5 text-red-500" />
            <div>
              <div className="text-2xl font-bold text-gray-900">{result.videos?.length || 0}</div>
              <div className="text-gray-600">Videos</div>
            </div>
          </div>
          <div className="bg-white p-3 rounded">
            <div className="text-2xl font-bold text-gray-900">{result.socialProfiles?.length || 0}</div>
            <div className="text-gray-600">Social Profiles</div>
          </div>
        </div>
      </div>

      {/* Key Facts */}
      {result.keyFacts && result.keyFacts.length > 0 && (
        <div className="space-y-2">
          <h2 className="font-semibold text-gray-900">Key Facts</h2>
          <ul className="list-disc list-inside text-sm text-gray-700 space-y-1">
            {result.keyFacts.map((fact, index) => (
              <li key={index}>{fact}</li>
            ))}
          </ul>
        </div>
      )}

      {/* News Articles */}
      {result.newsArticles && result.newsArticles.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">News & Articles</h2>
          {result.newsArticles.slice(0, 5).map((article, index) => (
            <a
              key={index}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border rounded-lg p-3 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 text-sm">{article.title}</h3>
                  <p className="text-xs text-gray-500 mt-1">{article.source}{article.date && ` • ${article.date}`}</p>
                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">{article.snippet}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Images */}
      {result.images && result.images.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Images</h2>
          <div className="grid grid-cols-3 gap-2">
            {result.images.slice(0, 6).map((image, index) => (
              <a
                key={index}
                href={image.url}
                target="_blank"
                rel="noopener noreferrer"
                className="aspect-square bg-gray-100 rounded-lg overflow-hidden"
              >
                <img
                  src={image.url}
                  alt={image.caption || `Image ${index + 1}`}
                  className="w-full h-full object-cover hover:opacity-80 transition-opacity"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Videos */}
      {result.videos && result.videos.length > 0 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-gray-900">Videos</h2>
          {result.videos.slice(0, 3).map((video, index) => (
            <a
              key={index}
              href={video.url}
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-white border rounded-lg p-3 hover:shadow-md transition-shadow"
            >
              <div className="flex items-center gap-3">
                <Video className="w-8 h-8 text-red-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium text-gray-900 text-sm truncate">{video.title}</h3>
                  <p className="text-xs text-gray-500">{video.platform}{video.duration && ` • ${video.duration}`}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0" />
              </div>
            </a>
          ))}
        </div>
      )}

      {/* Sources Badge */}
      {result.sources && result.sources.length > 0 && (
        <div>
          <MoreInfoSheet sources={result.sources}>
            <Badge variant="secondary" className="text-sm cursor-pointer hover:bg-gray-200 transition-colors">
              {result.sources.length} sources
            </Badge>
          </MoreInfoSheet>
        </div>
      )}

      {/* Summary */}
      <div className="space-y-2">
        <h2 className="font-semibold text-gray-900">Summary</h2>
        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{result.summary}</p>
      </div>
    </main>
  );
}
